import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { CognitoPasswordAuth } from "../auth/cognito-password.js";
import { AuthScreens } from "../auth/AuthScreens.js";
import { navigate, useRoutePath } from "../router.js";

/**
 * Admin console (/admin/*), served from the same SPA but OUTSIDE the tenant
 * AuthGate: an admin signs in with their normal Cognito credentials, and the
 * isolated admin API decides (from DB-backed membership) whether they are an
 * org_admin. Non-admins are told they lack access; they never see the queue.
 *
 *   /admin/sign-in   — branded admin sign-in (reuses the password auth form)
 *   /admin/dashboard — pending access requests: approve (grant a role) or deny
 *
 * The admin API base URL comes from /config.json (adminApiBaseUrl); auth config
 * (public client id + Cognito IDP endpoint) is the same cognito-password block
 * the app uses.
 */

interface AdminConfig {
  adminApiBaseUrl: string;
  clientId: string;
  idpUrl: string;
}

interface AccessRequest {
  request_id: string;
  user_sub: string;
  email: string | null;
  message: string | null;
  created_at: string;
}

const ROLES = ["viewer", "finance", "app_owner", "platform_ops", "migration_lead", "finops_lead", "executive", "org_admin"] as const;

async function loadAdminConfig(): Promise<AdminConfig> {
  const res = await fetch("/config.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`Configuration unavailable (${res.status}).`);
  const cfg = (await res.json()) as { adminApiBaseUrl?: string; auth?: { mode?: string; clientId?: string; idpUrl?: string } };
  if (!cfg.adminApiBaseUrl) throw new Error("Admin API is not configured for this deployment.");
  if (cfg.auth?.mode !== "cognito-password" || !cfg.auth.clientId || !cfg.auth.idpUrl) {
    throw new Error("Admin sign-in requires password auth, which is not configured here.");
  }
  return { adminApiBaseUrl: cfg.adminApiBaseUrl.replace(/\/$/, ""), clientId: cfg.auth.clientId, idpUrl: cfg.auth.idpUrl };
}

function buildAuth(cfg: AdminConfig): CognitoPasswordAuth {
  return new CognitoPasswordAuth({
    config: { clientId: cfg.clientId, idpUrl: cfg.idpUrl },
    storage: window.sessionStorage,
    fetch: window.fetch.bind(window),
    now: () => Date.now(),
  });
}

async function adminApi<T>(cfg: AdminConfig, auth: CognitoPasswordAuth, path: string, init?: RequestInit): Promise<T> {
  const token = await auth.currentToken();
  if (!token) throw new Error("Your session has expired. Please sign in again.");
  const res = await fetch(`${cfg.adminApiBaseUrl}${path}`, {
    ...init,
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status}).`);
  return data as T;
}

type Gate = { kind: "loading" } | { kind: "error"; message: string } | { kind: "signin"; reason?: string } | { kind: "notadmin"; email: string | null } | { kind: "admin"; email: string | null; tenants: AdminTenant[] };

export function AdminApp(): ReactNode {
  const path = useRoutePath();
  const [cfg, setCfg] = useState<AdminConfig | null>(null);
  const [gate, setGate] = useState<Gate>({ kind: "loading" });
  const auth = useMemo(() => (cfg ? buildAuth(cfg) : null), [cfg]);

  const checkAdmin = useCallback(
    async (c: AdminConfig, a: CognitoPasswordAuth) => {
      setGate({ kind: "loading" });
      try {
        const me = await adminApi<{ isAdmin: boolean; email: string | null; tenants: AdminTenant[] }>(c, a, "/me");
        if (me.isAdmin && me.tenants.length > 0) {
          setGate({ kind: "admin", email: me.email, tenants: me.tenants });
          if (path === "/admin" || path === "/admin/sign-in") navigate("/admin/dashboard");
        } else {
          setGate({ kind: "notadmin", email: me.email });
        }
      } catch (e) {
        a.clear();
        setGate({ kind: "signin", reason: (e as Error).message });
      }
    },
    [path]
  );

  useEffect(() => {
    void (async () => {
      let c: AdminConfig;
      try {
        c = await loadAdminConfig();
      } catch (e) {
        return setGate({ kind: "error", message: (e as Error).message });
      }
      setCfg(c);
      const a = buildAuth(c);
      if (a.session()) await checkAdmin(c, a);
      else setGate({ kind: "signin" });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = useCallback(() => {
    auth?.clear();
    setGate({ kind: "signin" });
    navigate("/admin/sign-in");
  }, [auth]);

  if (gate.kind === "loading") return <Centered><p className="cfo-admin-muted">Loading…</p></Centered>;
  if (gate.kind === "error") return <Centered><h1 className="cfo-admin-h1">Admin</h1><p className="cfo-admin-alert">{gate.message}</p></Centered>;

  if (gate.kind === "signin") {
    return cfg && auth ? (
      <div className="cfo-admin-shell">
        <style>{ADMIN_CSS}</style>
        <div className="cfo-admin-badge">Admin Console · Restricted</div>
        <AuthScreens auth={auth} onAuthenticated={() => void checkAdmin(cfg, auth)} {...(gate.reason ? { reason: gate.reason } : {})} />
      </div>
    ) : (
      <Centered><p className="cfo-admin-muted">Loading…</p></Centered>
    );
  }

  if (gate.kind === "notadmin") {
    return (
      <Centered>
        <h1 className="cfo-admin-h1">Admin access only</h1>
        <p className="cfo-admin-muted">
          You are signed in{gate.email ? ` as ${gate.email}` : ""}, but your account is not an administrator of any workspace. Ask an existing org admin to grant you the org_admin role.
        </p>
        <button type="button" className="cfo-admin-btn-ghost" onClick={signOut}>Sign out</button>
      </Centered>
    );
  }

  // admin
  return cfg && auth ? <AdminDashboard cfg={cfg} auth={auth} email={gate.email} tenants={gate.tenants} onSignOut={signOut} /> : null;
}

interface AdminTenant {
  tenantId: string;
  name: string;
}

interface Invitation {
  token: string;
  tenantName: string;
  role: string;
  invitedEmail: string | null;
  expiresAt: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SELECTED_KEY = "corefinops.admin.tenant";

/** The one-time join link. The token travels in the URL FRAGMENT: it is never sent to a server or written to access logs. */
export function inviteLink(origin: string, token: string): string {
  return `${origin}/#invite=${encodeURIComponent(token)}`;
}

function AdminDashboard({ cfg, auth, email, tenants, onSignOut }: { cfg: AdminConfig; auth: CognitoPasswordAuth; email: string | null; tenants: AdminTenant[]; onSignOut: () => void }): ReactNode {
  const [tenantId, setTenantId] = useState<string>(() => {
    try {
      const saved = window.sessionStorage.getItem(SELECTED_KEY);
      if (saved && tenants.some((t) => t.tenantId === saved)) return saved;
    } catch {
      /* storage unavailable: fall back to the first workspace */
    }
    return tenants[0]!.tenantId;
  });
  const tenant = tenants.find((t) => t.tenantId === tenantId) ?? tenants[0]!;
  const [requests, setRequests] = useState<AccessRequest[] | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | undefined>();
  const [inviteRole, setInviteRole] = useState<string>("viewer");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setError(undefined);
    setRequests(null);
    try {
      const r = await adminApi<{ requests: AccessRequest[] }>(cfg, auth, `/access-requests?tenantId=${encodeURIComponent(tenantId)}`);
      setRequests(r.requests);
    } catch (e) {
      setError((e as Error).message);
      setRequests([]);
    }
  }, [cfg, auth, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const chooseTenant = (id: string) => {
    setTenantId(id);
    setInvitation(null);
    setNotice(undefined);
    try {
      window.sessionStorage.setItem(SELECTED_KEY, id);
    } catch {
      /* storage unavailable: the choice just is not remembered */
    }
  };

  const decide = async (req: AccessRequest, action: "approve" | "deny") => {
    setBusyId(req.request_id);
    setError(undefined);
    setNotice(undefined);
    try {
      if (action === "approve") {
        const role = roles[req.request_id] ?? "viewer";
        await adminApi(cfg, auth, "/access-requests/approve", { method: "POST", body: JSON.stringify({ tenantId, requestId: req.request_id, role }) });
        setNotice(`Approved ${req.email ?? req.user_sub} as ${role} in ${tenant.name}.`);
      } else {
        await adminApi(cfg, auth, "/access-requests/deny", { method: "POST", body: JSON.stringify({ tenantId, requestId: req.request_id }) });
        setNotice(`Denied ${req.email ?? req.user_sub}.`);
      }
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const invite = async () => {
    const addr = inviteEmail.trim();
    if (addr && !EMAIL_RE.test(addr)) {
      setError("Enter a valid email address, or leave it empty to allow anyone with the link.");
      return;
    }
    setInviting(true);
    setError(undefined);
    setNotice(undefined);
    setCopied(false);
    try {
      const r = await adminApi<Invitation>(cfg, auth, "/invitations", {
        method: "POST",
        body: JSON.stringify({ tenantId, role: inviteRole, ...(addr ? { email: addr } : {}) }),
      });
      setInvitation(r);
      setInviteEmail("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setInviting(false);
    }
  };

  const link = invitation ? inviteLink(window.location.origin, invitation.token) : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="cfo-admin-app">
      <style>{ADMIN_CSS}</style>
      <header className="cfo-admin-top">
        <div className="cfo-admin-brand">
          <img className="cfo-admin-logo" src="/apple-touch-icon.png" alt="" aria-hidden="true" />
          <span>CoreFinOps <span className="cfo-admin-badge-inline">Admin</span></span>
        </div>
        <div className="cfo-admin-user">
          <span className="cfo-admin-muted">{email}</span>
          <button type="button" className="cfo-admin-btn-ghost" onClick={() => void load()}>Refresh</button>
          <button type="button" className="cfo-admin-btn-ghost" onClick={onSignOut}>Sign out</button>
        </div>
      </header>

      <main className="cfo-admin-main">
        <div className="cfo-admin-row">
          <label htmlFor="admin-workspace" className="cfo-admin-label">Workspace</label>
          {tenants.length > 1 ? (
            <select id="admin-workspace" className="cfo-admin-select" value={tenantId} onChange={(e) => chooseTenant(e.target.value)}>
              {tenants.map((t) => <option key={t.tenantId} value={t.tenantId}>{t.name}</option>)}
            </select>
          ) : (
            <strong id="admin-workspace">{tenant.name}</strong>
          )}
        </div>

        {notice ? <p className="cfo-admin-ok" role="status">{notice}</p> : null}
        {error ? <p className="cfo-admin-alert" role="alert">{error}</p> : null}

        <section aria-labelledby="invite-h" className="cfo-admin-panel">
          <h2 id="invite-h" className="cfo-admin-h2">Invite a member</h2>
          <p className="cfo-admin-muted">Creates a one-time link for <strong>{tenant.name}</strong> that expires in 7 days. Add their email to make it work only for that verified address.</p>
          <div className="cfo-admin-form">
            <label className="cfo-admin-label" htmlFor="invite-role">Role</label>
            <select id="invite-role" className="cfo-admin-select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
              {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
            </select>
            <label className="cfo-admin-label" htmlFor="invite-email">Email (optional)</label>
            <input id="invite-email" className="cfo-admin-input" type="email" inputMode="email" autoComplete="off" placeholder="name@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
            <button type="button" className="cfo-admin-btn" disabled={inviting} onClick={() => void invite()}>{inviting ? "Creating…" : "Create invitation"}</button>
          </div>
          {invitation ? (
            <div className="cfo-admin-invite" role="status">
              <p>
                Invitation for <strong>{invitation.tenantName}</strong> as <strong>{invitation.role}</strong>
                {invitation.invitedEmail ? <> — only for <strong>{invitation.invitedEmail}</strong></> : null}. Expires {new Date(invitation.expiresAt).toLocaleString()}.
              </p>
              <p className="cfo-admin-muted">Shown once. Copy it now; it cannot be displayed again.</p>
              <input className="cfo-admin-input cfo-admin-mono" readOnly aria-label="Invitation link" value={link} onFocus={(e) => e.currentTarget.select()} />
              <div className="cfo-admin-actions">
                <button type="button" className="cfo-admin-btn-ghost" onClick={() => void copy()}>{copied ? "Copied" : "Copy link"}</button>
                <span className="cfo-admin-muted">or give them the code: </span>
                <code data-testid="invite-code" className="cfo-admin-mono">{invitation.token}</code>
              </div>
            </div>
          ) : null}
        </section>

        <h2 className="cfo-admin-h2">Access requests</h2>
        <p className="cfo-admin-muted">People who signed in and asked to join <strong>{tenant.name}</strong>. Approve to grant them a role, or deny.</p>

        {requests === null ? (
          <p className="cfo-admin-muted">Loading requests…</p>
        ) : requests.length === 0 ? (
          <div className="cfo-admin-empty">No pending requests for {tenant.name}.</div>
        ) : (
          <table className="cfo-admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Requested</th>
                <th>Message</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.request_id}>
                  <td>{r.email ?? <span className="cfo-admin-muted">{r.user_sub.slice(0, 12)}…</span>}</td>
                  <td className="cfo-admin-muted">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="cfo-admin-msg">{r.message || <span className="cfo-admin-muted">—</span>}</td>
                  <td>
                    <select
                      className="cfo-admin-select"
                      aria-label={`Role for ${r.email ?? r.user_sub}`}
                      value={roles[r.request_id] ?? "viewer"}
                      onChange={(e) => setRoles((m) => ({ ...m, [r.request_id]: e.target.value }))}
                      disabled={busyId === r.request_id}
                    >
                      {ROLES.map((role) => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </td>
                  <td className="cfo-admin-actions">
                    <button type="button" className="cfo-admin-btn" disabled={busyId === r.request_id} onClick={() => void decide(r, "approve")}>
                      {busyId === r.request_id ? "…" : "Approve"}
                    </button>
                    <button type="button" className="cfo-admin-btn-danger" disabled={busyId === r.request_id} onClick={() => void decide(r, "deny")}>
                      Deny
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}

function Centered({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="cfo-admin-shell">
      <style>{ADMIN_CSS}</style>
      <div className="cfo-admin-card">{children}</div>
    </div>
  );
}

// A deliberately DISTINCT identity from the tenant app (light + indigo "operator
// console", lock iconography) so it's visually obvious this is the restricted
// admin surface, not the product.
const ADMIN_CSS = `
.cfo-admin-shell{position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;padding:24px;overflow:auto;
  background:linear-gradient(180deg,#ecedfb 0%,#f5f6fd 42%,#ffffff 100%);font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#1f2433;}
.cfo-admin-shell::before{content:"";position:fixed;inset:0;pointer-events:none;background:radial-gradient(640px 320px at 50% -8%,rgba(79,70,229,.14),transparent 70%);}
.cfo-admin-badge{position:relative;align-self:center;display:inline-flex;align-items:center;gap:7px;font-size:.72rem;font-weight:700;letter-spacing:.16em;text-transform:uppercase;
  color:#4f46e5;border:1px solid #d7d8f5;border-radius:999px;padding:6px 14px;background:#fff;box-shadow:0 2px 10px rgba(79,70,229,.14);}
.cfo-admin-badge::before{content:"\\1F512";filter:grayscale(1);}
.cfo-admin-badge-inline{font-size:.6rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#4f46e5;border:1px solid #d7d8f5;border-radius:999px;padding:3px 9px;margin-left:8px;vertical-align:middle;background:#eef0fe;}
.cfo-admin-card{position:relative;width:100%;max-width:440px;background:#fff;border:1px solid #e7e8f3;border-radius:18px;padding:34px 30px;box-shadow:0 24px 60px rgba(49,46,129,.14);text-align:center;}
.cfo-admin-card::before{content:"";position:absolute;left:0;right:0;top:0;height:4px;border-radius:18px 18px 0 0;background:linear-gradient(90deg,#4f46e5,#7c74f1);}
.cfo-admin-h1{font-size:1.55rem;font-weight:750;letter-spacing:-.01em;margin:14px 0 8px;color:#1a1f2e;}
.cfo-admin-muted{color:#6b7280;font-size:.9rem;}
.cfo-admin-alert{color:#b42318;background:#fef3f2;border:1px solid #fecdca;border-radius:10px;padding:.6rem .85rem;font-size:.85rem;margin:12px 0;}
.cfo-admin-ok{color:#067647;background:#ecfdf3;border:1px solid #abefc6;border-radius:10px;padding:.6rem .85rem;font-size:.85rem;margin:12px 0;}
.cfo-admin-app{min-height:100vh;background:linear-gradient(180deg,#ecedfb 0,#f5f6fd 200px,#fafbfe 420px);color:#1f2433;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;}
.cfo-admin-top{display:flex;align-items:center;justify-content:space-between;padding:15px 28px;background:#fff;border-bottom:1px solid #e7e8f3;box-shadow:0 1px 3px rgba(49,46,129,.06);position:sticky;top:0;z-index:5;}
.cfo-admin-brand{display:flex;align-items:center;gap:11px;font-weight:750;color:#1a1f2e;}
.cfo-admin-logo{width:30px;height:30px;border-radius:9px;box-shadow:0 0 0 1px #e7e8f3;}
.cfo-admin-user{display:flex;align-items:center;gap:12px;}
.cfo-admin-main{max-width:1020px;margin:0 auto;padding:30px 28px;}
.cfo-admin-empty{margin-top:24px;padding:48px;text-align:center;border:1px dashed #d4d6ea;border-radius:14px;color:#6b7280;background:#fcfcff;}
.cfo-admin-table{width:100%;border-collapse:collapse;margin-top:18px;background:#fff;border:1px solid #e7e8f3;border-radius:14px;overflow:hidden;box-shadow:0 1px 2px rgba(16,24,40,.04),0 1px 3px rgba(16,24,40,.06);}
.cfo-admin-table th{text-align:left;font-size:.7rem;letter-spacing:.06em;text-transform:uppercase;color:#8089a0;padding:13px 16px;border-bottom:1px solid #eceef6;background:#fbfbfe;}
.cfo-admin-table td{padding:13px 16px;border-bottom:1px solid #f1f2f8;font-size:.9rem;vertical-align:middle;color:#2a3142;}
.cfo-admin-table tbody tr:last-child td{border-bottom:none;}
.cfo-admin-table tbody tr:hover{background:#f8f8fd;}
.cfo-admin-msg{max-width:260px;}
.cfo-admin-actions{display:flex;gap:8px;justify-content:flex-end;}
.cfo-admin-select{background:#fff;color:#2a3142;border:1px solid #d4d6ea;border-radius:8px;padding:.42rem .55rem;font-size:.85rem;}
.cfo-admin-btn{background:linear-gradient(135deg,#4f46e5,#6366f1);color:#fff;border:none;border-radius:8px;padding:.46rem .95rem;font-weight:600;cursor:pointer;box-shadow:0 2px 8px rgba(79,70,229,.25);}
.cfo-admin-btn:disabled{opacity:.5;cursor:not-allowed;box-shadow:none;}
.cfo-admin-btn-danger{background:#fff;color:#b42318;border:1px solid #fbbcb3;border-radius:8px;padding:.46rem .95rem;font-weight:600;cursor:pointer;}
.cfo-admin-btn-danger:hover{background:#fef3f2;}
.cfo-admin-btn-ghost{background:#fff;border:1px solid #e0e2ef;color:#4b5366;border-radius:8px;padding:.42rem .85rem;cursor:pointer;font-size:.85rem;}
.cfo-admin-btn-ghost:hover{background:#f5f6fd;border-color:#cdd0ea;}
.cfo-admin-row{display:flex;align-items:center;gap:12px;margin-bottom:6px;}
.cfo-admin-label{font-size:.78rem;font-weight:650;color:#4b5366;letter-spacing:.02em;}
.cfo-admin-h2{font-size:1.1rem;font-weight:700;margin:26px 0 6px;color:#1a1f2e;}
.cfo-admin-panel{background:#fff;border:1px solid #e7e8f3;border-radius:14px;padding:18px 20px;margin-top:16px;box-shadow:0 1px 2px rgba(16,24,40,.04);}
.cfo-admin-form{display:grid;grid-template-columns:auto 1fr;gap:10px 14px;align-items:center;margin-top:12px;max-width:560px;}
.cfo-admin-form .cfo-admin-btn{grid-column:2;justify-self:start;}
.cfo-admin-input{border:1px solid #d4d6ea;border-radius:8px;padding:.5rem .65rem;font-size:.9rem;width:100%;box-sizing:border-box;font-family:inherit;}
.cfo-admin-invite{margin-top:16px;padding:14px;border:1px solid #abefc6;background:#f6fef9;border-radius:10px;}
.cfo-admin-mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:.82rem;word-break:break-all;}
@media (max-width:640px){.cfo-admin-form{grid-template-columns:1fr}.cfo-admin-form .cfo-admin-btn{grid-column:1}.cfo-admin-main{padding:20px 16px}.cfo-admin-top{padding:12px 16px}.cfo-admin-table{display:block;overflow-x:auto}}
`;
